import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChefHat, Clock, Sparkles, Plus, AlertTriangle, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Smart meal-suggestion dialog. Shows AI-recommended dinners based on the
 * boat's current pantry + expiry dates. Each suggestion can be added to the
 * weekly plan with one click via the `onAddToWeek` callback.
 */
export default function SmartMealSuggestDialog({
  open,
  onOpenChange,
  onAddToWeek,
}) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [meta, setMeta] = useState({ pantry_count: 0, expiring_soon_count: 0 });
  const [extraNotes, setExtraNotes] = useState("");
  const [seenRecipeIds, setSeenRecipeIds] = useState([]);

  const fetchSuggestions = async (rerollExcludeIds = []) => {
    setLoading(true);
    try {
      const res = await api.suggestRecipesFromPantry({
        max_suggestions: 5,
        extra_notes: extraNotes,
        exclude_recipe_ids: rerollExcludeIds,
      });
      const data = res.data || {};
      const next = data.suggestions || [];
      setSuggestions(next);
      setMeta({
        pantry_count: data.pantry_count || 0,
        expiring_soon_count: data.expiring_soon_count || 0,
      });
      // Track which existing recipes we've shown so reroll can skip them
      setSeenRecipeIds((prev) => [
        ...prev,
        ...next.filter((s) => s.recipe_id).map((s) => s.recipe_id),
      ]);
      if (next.length === 0) {
        toast.info("AI fant ingen middagar du kan lage no. Legg til fleire varer i beholdninga.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikkje hente forslag");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on first open
  if (open && suggestions.length === 0 && !loading) {
    fetchSuggestions();
  }

  const handleClose = (next) => {
    if (!next) {
      setSuggestions([]);
      setSeenRecipeIds([]);
      setExtraNotes("");
    }
    onOpenChange(next);
  };

  const difficultyColor = (d) => ({
    "lett": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "medium": "bg-amber-50 text-amber-700 border-amber-200",
    "utfordrande": "bg-rose-50 text-rose-700 border-rose-200",
  }[d] || "bg-stone-50 text-stone-600 border-stone-200");

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="smart-meal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-stone-800">
            <ChefHat className="h-5 w-5 text-violet-600" />
            Smart middag-forslag
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            AI les beholdninga di og foreslår middagar du kan lage no — varer som snart utløper får prioritet.
            {meta.pantry_count > 0 && (
              <span className="block text-xs mt-1 text-stone-400">
                {meta.pantry_count} varer i beholdning · {meta.expiring_soon_count} snart utløper
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Extra notes input + reroll */}
        <div className="flex items-center gap-2 mb-3">
          <input
            type="text"
            value={extraNotes}
            onChange={(e) => setExtraNotes(e.target.value)}
            placeholder="Eks: ingen fisk i kveld, raskt"
            className="flex-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
            data-testid="meal-suggest-notes"
          />
          <button
            onClick={() => fetchSuggestions(seenRecipeIds)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 hover:bg-violet-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            data-testid="meal-suggest-reroll"
            title="Få nye forslag (utan å vise dei same igjen)"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Tenker…" : "Nye forslag"}
          </button>
        </div>

        {loading && suggestions.length === 0 && (
          <div className="text-center py-10 text-stone-500" data-testid="meal-suggest-loading">
            <Sparkles className="h-8 w-8 mx-auto mb-2 text-violet-400 animate-pulse" />
            AI bløtleg i beholdning og oppskrifter…
          </div>
        )}

        {!loading && suggestions.length === 0 && (
          <div className="text-center py-10 text-stone-500" data-testid="meal-suggest-empty">
            <ChefHat className="h-8 w-8 mx-auto mb-2 text-stone-300" />
            Ingen forslag enno. Legg til varer i beholdninga først.
          </div>
        )}

        <div className="space-y-3" data-testid="meal-suggest-list">
          {suggestions.map((s, idx) => (
            <div
              key={`${s.recipe_id || "ai"}-${idx}`}
              className="border border-stone-200 rounded-lg p-3 sm:p-4 bg-white hover:border-violet-300 transition-colors"
              data-testid={`meal-suggestion-${idx}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-semibold text-stone-800 text-base">{s.title}</h3>
                    {s.ai_invented && (
                      <span className="text-[10px] uppercase tracking-wider font-bold text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-full">
                        AI
                      </span>
                    )}
                    {s.difficulty && (
                      <span className={`text-[10px] uppercase tracking-wider font-semibold border px-1.5 py-0.5 rounded-full ${difficultyColor(s.difficulty)}`}>
                        {s.difficulty}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-stone-500 mb-2">
                    {s.estimated_time_minutes ? (
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {s.estimated_time_minutes} min</span>
                    ) : null}
                    {s.priority_reason && (
                      <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" /> {s.priority_reason}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => onAddToWeek && onAddToWeek(s)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 px-3 py-2 text-xs font-medium text-white whitespace-nowrap"
                  data-testid={`meal-suggest-add-${idx}`}
                  title="Legg til på vekeplanen"
                >
                  <Plus className="h-3 w-3" /> Legg på plan
                </button>
              </div>

              {s.uses_from_pantry && s.uses_from_pantry.length > 0 && (
                <div className="mt-2 text-xs">
                  <span className="text-stone-500 font-medium uppercase tracking-wider text-[10px]">Bruker frå lager:</span>{" "}
                  <span className="text-emerald-700">
                    {s.uses_from_pantry.slice(0, 6).map((p, i) => (
                      <span key={i} className="inline-flex items-center gap-1 mr-2">
                        <Check className="h-3 w-3" /> {p}
                      </span>
                    ))}
                    {s.uses_from_pantry.length > 6 && <span className="text-stone-400"> +{s.uses_from_pantry.length - 6}</span>}
                  </span>
                </div>
              )}

              {s.missing_ingredients && s.missing_ingredients.length > 0 && (
                <div className="mt-1 text-xs">
                  <span className="text-stone-500 font-medium uppercase tracking-wider text-[10px]">Manglar:</span>{" "}
                  <span className="text-rose-700">{s.missing_ingredients.slice(0, 8).join(", ")}</span>
                </div>
              )}

              {s.ai_invented && s.short_instructions && s.short_instructions.length > 0 && (
                <ol className="mt-2 text-xs text-stone-600 pl-4 list-decimal space-y-0.5">
                  {s.short_instructions.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
